import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useReportTabs } from '@/contexts/ReportTabsContext';
import { resolveWorkspaceTab } from '@/lib/workspaceTabs';
import { api } from '@/lib/api';
import { useTerms, getTerm } from '@/lib/branding';
import {
  X, GitBranch, FileText, Search, Columns2, Pin, PinOff,
  ChevronDown, ChevronUp, Plus, AlertTriangle, PanelBottom, Pencil,
} from 'lucide-react';

// Per-type identity: icon + colour so Insights / Models / Explore read as
// distinct groups at a glance.
const TYPE_META = {
  insight: { icon: FileText, label: 'Insights', dot: 'bg-emerald-400', groupText: 'text-emerald-400/70',
    idle: 'text-emerald-300/80 hover:bg-emerald-500/10', active: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  model: { icon: GitBranch, label: 'Models', dot: 'bg-violet-400', groupText: 'text-violet-400/70',
    idle: 'text-violet-300/80 hover:bg-violet-500/10', active: 'bg-violet-500/15 text-violet-300 border-violet-500/40' },
  explore: { icon: Search, label: 'Explore', dot: 'bg-sky-400', groupText: 'text-sky-400/70',
    idle: 'text-sky-300/80 hover:bg-sky-500/10', active: 'bg-sky-500/15 text-sky-300 border-sky-500/40' },
};
const TYPE_ORDER = ['insight', 'model', 'explore'];

// The Workbench dock — a slim, IDE-style bar pinned to the bottom holding the
// live working sessions (insights, models, AI Explore). Everything else in the
// app is a plain screen and never appears here.
export default function WorkbenchDock() {
  useTerms();
  const ws = useWorkspace();
  const rt = useReportTabs();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [renaming, setRenaming] = useState(null); // key being renamed
  const [renameVal, setRenameVal] = useState('');
  const [names, setNames] = useState({}); // model_id -> model name (labels Models + Explore)

  const startRename = (e, tab) => {
    e.stopPropagation();
    setRenaming(tab.key);
    setRenameVal(displayTitle(tab));
  };
  const commitRename = () => {
    if (renaming) ws.renameTab(renaming, renameVal);
    setRenaming(null); setRenameVal('');
  };

  useEffect(() => {
    let alive = true;
    api.getSemanticModels().then(ms => {
      if (!alive) return;
      const m = {}; (ms || []).forEach(x => { m[x.model_id] = x.name; });
      setNames(m);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const currentKey = resolveWorkspaceTab(location.pathname, location.search)?.key;
  const grouped = useMemo(() => {
    const g = { insight: [], model: [], explore: [] };
    (ws?.tabs || []).forEach(t => { (g[t.type] || (g[t.type] = [])).push(t); });
    return g;
  }, [ws?.tabs]);

  if (!ws || ws.tabs.length === 0) return null;

  const activeTab = ws.tabs.find(t => t.key === (currentKey || ws.activeKey));

  // Prefer the real resource name; fall back to whatever the page set.
  const displayTitle = (tab) => {
    if (tab.customTitle) return tab.customTitle;
    if (tab.type === 'model') {
      const id = tab.key.replace(/^model:/, '');
      return names[id] || (tab.title && tab.title !== 'Model' ? tab.title : 'Untitled model');
    }
    if (tab.type === 'explore') {
      const id = tab.key.replace(/^explore:/, '');
      const t = tab.title;
      // Once the page sets the first question as the title, show it; otherwise
      // show the model name.
      if (t && t !== 'AI Explore') return t.replace(/^Explore · /, '');
      return names[id] || 'Explore';
    }
    if (tab.title === 'New Insight') return `New ${getTerm('insights', 'singular')}`;
    return tab.title && tab.title !== 'Insight' ? tab.title : `Untitled ${getTerm('insights', 'singular', 'lower')}`;
  };

  const navAwayFrom = (closedKey) => {
    const rest = ws.tabs.filter(t => t.key !== closedKey).sort((a, b) => (b._t || 0) - (a._t || 0));
    navigate(rest[0] ? rest[0].path : '/dashboard');
  };
  // Keep the Report Builder's own tab registry in sync: closing an insight
  // chip here must also tear down its builder instance + discard its draft.
  const closeMatchingReportTab = (wsKey) => {
    if (!rt || !wsKey.startsWith('insight:')) return;
    const rest = wsKey.slice('insight:'.length); // "<rid>" | "draft:<token>" | "new"
    let t = null;
    if (rest.startsWith('draft:')) t = rt.tabs.find(x => x.id === rest);
    else if (rest === 'new') t = rt.tabs.find(x => !x.reportId && !x.id.startsWith('draft:'));
    else t = rt.tabs.find(x => x.reportId === rest);
    if (t) rt.closeTab(t.id);
  };
  const handleClose = (e, tab) => {
    e.stopPropagation();
    const wasCurrent = tab.key === currentKey;
    const closedNow = ws.requestClose(tab.key);
    if (closedNow) {
      closeMatchingReportTab(tab.key);
      if (wasCurrent) navAwayFrom(tab.key);
    }
  };
  const handleSplit = (e, tab) => {
    e.stopPropagation();
    if (!activeTab || activeTab.key === tab.key) return;
    ws.openSplit({ path: activeTab.path, title: displayTitle(activeTab) }, { path: tab.path, title: displayTitle(tab) });
  };

  const pending = ws.pendingCloseTab;
  const pendingHasSaver = pending && ws.saverKeys.has(pending.key);
  const resolvePending = async (action) => {
    const wasCurrent = pending && pending.key === currentKey;
    const closingKey = pending?.key;
    await ws.resolvePendingClose(action);
    if (action !== 'cancel' && closingKey) {
      closeMatchingReportTab(closingKey);
      if (wasCurrent) navAwayFrom(closingKey);
    }
  };

  return (
    <>
      <div
        className="shrink-0 border-t border-border/60"
        style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }}
        data-testid="workbench-dock"
      >
        {collapsed ? (
          <button
            onClick={() => setCollapsed(false)}
            className="w-full flex items-center gap-1.5 px-3 h-8 text-[11px] font-semibold text-muted-foreground hover:text-foreground transition-colors"
            data-testid="workbench-collapse"
            title="Expand Workbench"
          >
            <PanelBottom className="w-3.5 h-3.5" /> Workbench
            <span className="text-muted-foreground/80 font-normal">({ws.tabs.length})</span>
            <ChevronUp className="w-3 h-3" />
          </button>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1.5">
            <button
              onClick={() => setCollapsed(true)}
              className="flex items-center gap-1 text-[10px] font-semibold text-muted-foreground/70 hover:text-foreground transition-colors shrink-0"
              data-testid="workbench-collapse"
              title="Collapse Workbench"
            >
              <PanelBottom className="w-3.5 h-3.5" />
              <ChevronDown className="w-3 h-3" />
            </button>

            {/* session chips grouped by type */}
            <div className="flex items-stretch gap-2 overflow-x-auto no-scrollbar flex-1" data-testid="workbench-sessions">
              {TYPE_ORDER.filter(type => grouped[type]?.length).map((type, gi) => {
                const meta = TYPE_META[type];
                const TypeIcon = meta.icon;
                return (
                  <div key={type} className={`flex items-center gap-1.5 shrink-0 ${gi > 0 ? 'pl-2 border-l border-border/40' : ''}`}>
                    <span className={`flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide ${meta.groupText}`}>
                      <TypeIcon className="w-3 h-3" /> {meta.label}
                    </span>
                    {grouped[type].map(tab => {
                      const active = tab.key === currentKey;
                      const dirty = ws.dirtyKeys.has(tab.key);
                      return (
                        <div
                          key={tab.key}
                          onClick={() => renaming === tab.key ? null : navigate(tab.path)}
                          onDoubleClick={(e) => startRename(e, tab)}
                          title={displayTitle(tab)}
                          data-testid={`workbench-session-${tab.key}`}
                          className={`group flex items-center gap-1.5 pl-2 pr-2 h-7 rounded-md text-xs font-medium whitespace-nowrap cursor-pointer transition-colors border max-w-[180px]
                            ${active ? meta.active : `bg-transparent border-transparent ${meta.idle}`}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${meta.dot}`} />
                          {renaming === tab.key ? (
                            <input
                              autoFocus
                              value={renameVal}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setRenameVal(e.target.value)}
                              onBlur={commitRename}
                              onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setRenaming(null); setRenameVal(''); } }}
                              className="bg-background/80 border border-border/50 rounded px-1 py-0.5 text-xs text-foreground outline-none w-28"
                              data-testid={`workbench-rename-input-${tab.key}`}
                            />
                          ) : (
                            <span className="truncate">{displayTitle(tab)}</span>
                          )}
                          {dirty && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" title="Unsaved changes" data-testid={`workbench-dirty-${tab.key}`} />}
                          {/* actions: hidden until hover so idle chips stay compact */}
                          <span className="hidden group-hover:flex items-center gap-0.5 -mr-1">
                            <button onClick={(e) => startRename(e, tab)} className="p-0.5 rounded text-muted-foreground/80 hover:text-foreground" title="Rename" data-testid={`workbench-rename-${tab.key}`}>
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); ws.togglePin(tab.key); }} className={`p-0.5 rounded ${tab.pinned ? 'text-foreground' : 'text-muted-foreground/80 hover:text-foreground'}`} title={tab.pinned ? 'Unpin' : 'Pin'} data-testid={`workbench-pin-${tab.key}`}>
                              {tab.pinned ? <Pin className="w-3 h-3" /> : <PinOff className="w-3 h-3" />}
                            </button>
                            {activeTab && activeTab.key !== tab.key && (
                              <button onClick={(e) => handleSplit(e, tab)} className="p-0.5 rounded text-muted-foreground/80 hover:text-foreground" title={`Side-by-side with ${displayTitle(activeTab)}`} data-testid={`workbench-split-${tab.key}`}>
                                <Columns2 className="w-3 h-3" />
                              </button>
                            )}
                            <button onClick={(e) => handleClose(e, tab)} className="p-0.5 rounded text-muted-foreground/80 hover:text-foreground" title={`Close ${displayTitle(tab)}`} data-testid={`workbench-close-${tab.key}`}>
                              <X className="w-3 h-3" strokeWidth={2} />
                            </button>
                          </span>
                          {/* pinned marker when not hovering */}
                          {tab.pinned && <Pin className="w-2.5 h-2.5 text-muted-foreground/70 group-hover:hidden shrink-0" />}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {ws.split && (
              <button onClick={() => ws.clearSplit()} className="text-[10px] text-amber-400 hover:text-amber-300 flex items-center gap-1 shrink-0" data-testid="workbench-exit-split">
                <Columns2 className="w-3 h-3" /> Exit split
              </button>
            )}
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('workbench:open-switcher'))}
              className="shrink-0 flex items-center gap-1 h-7 px-2 rounded-md text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
              data-testid="workbench-new"
              title="Open or switch session"
            >
              <Plus className="w-3.5 h-3.5" /> Open
            </button>
          </div>
        )}
      </div>

      {/* unsaved-changes prompt */}
      {pending && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="unsaved-dialog">
          <div className="w-full max-w-sm rounded-2xl border border-border/60 bg-card shadow-2xl p-5">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-500/15 text-amber-400 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-foreground">Unsaved changes</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  “{displayTitle(pending)}” has changes that aren’t saved. Close it anyway?
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={() => resolvePending('cancel')} className="px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors" data-testid="unsaved-cancel">Keep editing</button>
              <button onClick={() => resolvePending('discard')} className="px-3 py-1.5 rounded-lg text-xs text-red-400 hover:bg-red-500/10 transition-colors" data-testid="unsaved-discard">Discard &amp; close</button>
              {pendingHasSaver && (
                <button onClick={() => resolvePending('save')} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors" data-testid="unsaved-save">Save &amp; close</button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
